import { type SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { EventNames } from 'constants/event-bus-events';
import { GlobalModal } from 'constants/global-modal';
import { PostMessageEvent } from 'constants/post-message-event';
import { QueryKey } from 'constants/query-key';
import { SubscriptionRouteEvent } from 'constants/subscription/event';
import { ToastAutoHideDelay, ToastContent } from 'constants/toasts';
import { EventPlace, trackGTMEvent, trackZapierEvent } from 'helpers/analytics';
import { doesCartIncludeApiUsageProPlusPackage, trackApiPackageBoughtEvents } from 'helpers/api-usage-package';
import { sendPostMessageToMarketplace } from 'helpers/post-message';
import { isCovidCoupon } from 'helpers/recurly';
import { getToastContent } from 'helpers/toast';
import type { RequestResult } from 'interfaces/api/client';
import type { Product, ProductsCart } from 'interfaces/product-cart';
import { BillingCycleType, type INewSubscription, type ISubscription } from 'interfaces/subscription';
import { ApiManager } from 'services/api/api-manager';
import { type ISubscriptionDTO } from 'services/api/subscription/interfaces';
import { SubscriptionSerializer } from 'services/api/subscription/serializer';
import { PlatformNamespace } from 'services/connectivity/configuration-api/properties/constants';
import { EventBus } from 'services/event-bus';
import { trackEvent } from 'services/event-tracking';
import { getQueryClient } from 'services/query-client/client';
import { QUERY_KEYS } from 'services/query-client/keys';
import { getIsGoogleAnalyticsInstalled } from 'store/entities/applications/selectors';
import { LicenseCustomPropertiesActions } from 'store/entities/license-custom-properties/actions';
import { getCurrentCart, getCurrentCartProductsToSubscribe } from 'store/entities/product-cart/selectors';
import { RecurlyActions } from 'store/entities/recurly/actions';
import { SubscriptionActionNames, SubscriptionActions } from 'store/entities/subscription/actions';
import { getEstimatedPlanPrice, getSubscriptionChange } from 'store/entities/subscription/selectors';
import { ApplicationsActions } from 'store/features/applications/actions';
import { GlobalModalActions } from 'store/features/global-modals/actions';
import { getLicenseId } from 'store/features/session/selectors';
import { ToastsActions } from 'store/features/toasts/actions';
import { ToastVariant } from 'store/features/toasts/interfaces';
import { type IActionWithPayload } from 'store/helper';
import { SubscriptionViewActions } from 'store/views/subscription/actions';
import { getIsNewCouponApplicable } from 'store/views/subscription/selectors';

import { realizeCurrentCart, refreshApplicationsData } from '../product-cart/realize-cart';

import { GTMEvents, SubscriptionEventFields } from './constants';
import {
  deserializeSubscriptionError,
  getTrackerId,
  mapSubscriptionToNewSubscription,
  refreshCouponData,
  saveAutomaticUpsellingEnabled,
  triggerSalesTracker,
} from './helpers';
import { AppsInstallationStatus } from './update-subscription';

function* trackSubscriptionCreateEvents(
  subscription: ISubscription,
  appsInstallationStatus: AppsInstallationStatus,
  installedAppsNumber?: number,
): SagaIterator {
  const { amount, seats, billingCycle } = subscription;
  const isGoogleAnalyticsInstalled: boolean = yield select(getIsGoogleAnalyticsInstalled);
  const licenseId: string = yield select(getLicenseId);
  const transactionId = `${licenseId}.${new Date().getTime()}`;

  const price = amount * seats * billingCycle;

  trackEvent(SubscriptionRouteEvent.FirstPaymentFinished, EventPlace.Subscription, {
    [SubscriptionEventFields.GoogleAnalytics]: isGoogleAnalyticsInstalled,
    [SubscriptionEventFields.AppsInstallationStatus]: appsInstallationStatus,
    [SubscriptionEventFields.BillingPeriod]: billingCycle,
    ...(appsInstallationStatus === AppsInstallationStatus.Success
      ? { [SubscriptionEventFields.AppsInstalled]: installedAppsNumber }
      : {}),
  });
  trackGTMEvent(GTMEvents.FirstPayment, {
    ecommerce: {
      purchase: {
        actionField: {
          id: transactionId,
          revenue: price,
          coupon: subscription.couponCode,
        },
        products: [
          {
            name: 'LiveChat - First payment',
            id: subscription.planCode,
            price: amount,
            quantity: subscription.seats,
          },
        ],
      },
    },
  });
  void trackZapierEvent(`/lzwx9g/?email=${encodeURIComponent(subscription.email)}`);
}

function* createSubscription({ payload }: IActionWithPayload<string, { threeDSecureTokenId: string }>): SagaIterator {
  const newSubscription: INewSubscription = yield select(getSubscriptionChange);
  const isNewCouponApplicable = yield select(getIsNewCouponApplicable);
  const estimatedPlanPrice: number = yield select(
    getEstimatedPlanPrice,
    newSubscription.seats,
    newSubscription.planCode,
  );
  const serializedSubscription = SubscriptionSerializer.serialize(newSubscription, { isNewCouponApplicable });

  // 1. Create subscription
  try {
    const { error }: RequestResult<unknown> = yield call(ApiManager.billingApiV3.createOrUpdateSubscription, {
      ...serializedSubscription,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      three_d_secure_action_result_token: payload.threeDSecureTokenId,
    });
    if (error) throw error;

    if (serializedSubscription.coupon_code && isCovidCoupon(serializedSubscription.coupon_code)) {
      yield put(LicenseCustomPropertiesActions.saveLicenseCustomProperty({ name: 'covid_coupon_used', value: '1' }));
    }

    yield call(saveAutomaticUpsellingEnabled);
    const queryClient = getQueryClient();
    yield call(
      queryClient.invalidateQueries.bind(queryClient),
      QUERY_KEYS[QueryKey.LicenseProperties](PlatformNamespace.BILLING),
    );

    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.SUBSCRIPTION_CREATED),
        kind: ToastVariant.Success,
        autoHideDelayTime: ToastAutoHideDelay.Long,
      }),
    );

    const isMonthlyNew = serializedSubscription.months === BillingCycleType.Monthly;
    const isAnnualNew = serializedSubscription.months === BillingCycleType.Annually12;

    if (isMonthlyNew) {
      triggerSalesTracker(getTrackerId('newMonthly'), estimatedPlanPrice.toString());
    }
    if (isAnnualNew) {
      triggerSalesTracker(getTrackerId('newAnnually'), estimatedPlanPrice.toString());
    }

    EventBus.emit(EventNames.SubscriptionChanged);
  } catch (e) {
    const { isRecurlyError, isCouponError, error } = deserializeSubscriptionError(e);
    if (isRecurlyError) {
      yield put(SubscriptionViewActions.setRecurlyError(error));
    }

    if (isCouponError) {
      yield put(RecurlyActions.redemptionError({ error }));
    }

    if (!isRecurlyError) {
      yield put(ToastsActions.createToast({ content: error, kind: ToastVariant.Error, autoHideDelayTime: undefined }));
    }
    trackEvent(SubscriptionRouteEvent.SubscriptionFailed, EventPlace.Subscription, { error });
    yield put(SubscriptionActions.createSubscriptionFailure({ error: JSON.stringify(e) }));

    return;
  }

  // 2. Load and deserialize new subscription
  const { result, error }: RequestResult<ISubscriptionDTO> = yield call(ApiManager.subscriptionApi.fetch);
  if (error) {
    yield put(SubscriptionViewActions.licenseDataRefreshFailure());

    return;
  }

  const deserializedSubscription = SubscriptionSerializer.deserialize(result);
  yield call(sendPostMessageToMarketplace, PostMessageEvent.RefreshInstalledApps);

  // 3. Install apps + refresh license / app data
  try {
    const cartProducts: Product[] = yield select(getCurrentCartProductsToSubscribe);
    const addonsToInstallNumber = cartProducts.length;
    const cartProductsIds = cartProducts.map(({ id }) => id);

    yield all([call(refreshCouponData, deserializedSubscription), call(realizeCurrentCart)]);
    yield call(refreshApplicationsData);
    // Mark purchased cart apps as selected
    yield put(ApplicationsActions.addCartApplicationsSelected(cartProductsIds));
    yield put(SubscriptionActions.createSubscriptionSuccess(deserializedSubscription));
    yield call(
      trackSubscriptionCreateEvents,
      deserializedSubscription,
      AppsInstallationStatus.Success,
      addonsToInstallNumber,
    );
    yield call(mapSubscriptionToNewSubscription, deserializedSubscription);

    trackApiPackageBoughtEvents(cartProducts);

    if (doesCartIncludeApiUsageProPlusPackage(cartProducts)) {
      yield put(GlobalModalActions.showModal(GlobalModal.ApiUsagePackageScheduleCall));
    }
  } catch (error) {
    const { error: err } = deserializeSubscriptionError(error);
    const currentCart: ProductsCart | null = yield select(getCurrentCart);

    yield call(trackSubscriptionCreateEvents, deserializedSubscription, AppsInstallationStatus.Failure);
    trackEvent(SubscriptionRouteEvent.CreateSubscriptionSideEffectFailed, EventPlace.Subscription, {
      err,
      cartId: currentCart?.id,
    });
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.SOMETHING_WENT_WRONG),
        kind: ToastVariant.Error,
        autoHideDelayTime: ToastAutoHideDelay.Long,
      }),
    );
  }
}

export function* createSubscriptionSaga(): SagaIterator {
  yield takeEvery(SubscriptionActionNames.CREATE_SUBSCRIPTION, createSubscription);
}
